import React, { useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
  modalConfirm,
  modalQuestionResolution,
  modalHint,
  modalHintShow
} from "../../utils/logger";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-orange.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaOrange from "../../assets/btnPistaOrange.png";
import imageHint from "../../assets/hints/imageHintOrange.png";
import imageHintUsed from "../../assets/hintUsed.png";
import unIntento from "../../assets/un-intento-orange.png"
import dosIntentos from "../../assets/dos-intentos-orange.png"

function Scenario2Clasificacion({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const QUIZ_ID = QUIZZES.CLASIFICACION_PACIENTE;
  const SCENARIO_ID = SCENARIOS.SCENARIO_2;

  const [selectedOption, setSelectedOption] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 🔸 Segundo intento con envío único
  const [attempt, setAttempt] = useState(1);               // 1..2
  const [blockedOptions, setBlockedOptions] = useState(new Set());
  const [pendingScore, setPendingScore] = useState(0);     // acumulado a enviar al final
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false); // pista se descuenta 1 sola vez

  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  const options = [
    { id: 1, text: "<h4 class='txtSemiBlack bold text-center mt-2'>CPSCm de alto riesgo y volumen, sincrónico</h4>", correct: true, explanation: "-----" },
    { id: 2, text: "<h4 class='txtSemiBlack bold text-center mt-2'>CPSCm de bajo volumen y de bajo riesgo, sincrónico</h4>", correct: false, explanation: "-----" },
    { id: 3, text: "<h4 class='txtSemiBlack bold text-center mt-2'>CPSCm de alto volumen metacrónico</h4>", correct: false, explanation: "-----" },
    { id: 4, text: "<h4 class='txtSemiBlack bold text-center mt-2'>CPRC no metastásico</h4>", correct: false, explanation: "-----" },
    { id: 5, text: "<h4 class='txtSemiBlack bold text-center mt-2'>CPRC metastásico sintomático</h4>", correct: false, explanation: "-----" },
    { id: 6, text: "<h4 class='txtSemiBlack bold text-center mt-2'>CPSCm de bajo volumen metacrónico</h4>", correct: false, explanation: "-----" },
    { id: 7, text: "<h4 class='txtSemiBlack bold text-center mt-2'>CPRC metastásico asintomático</h4>", correct: false, explanation: "-----" },
    { id: 8, text: "<h4 class='txtSemiBlack bold text-center mt-2'>CPSCm de riesgo intermedio, sincrónico</h4>", correct: false, explanation: "-----" },
    { id: 9, text: "<h4 class='txtSemiBlack bold text-center mt-2'>Cáncer de próstata localmente avanzado sin metástasis</h4>", correct: false, explanation: "-----" },
  ];

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));
    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore, // único envío con la suma final
        time: totalTime,
        lastVisited: ROUTES.SCENARIO2_METASTASIS,
        correct: correctFinal
      });

      if (res.status === 201) {
        const serverGroup = res.data?.group;
        if (serverGroup) dispatch({ type: "GROUP_SET", payload: serverGroup });
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO2_METASTASIS);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO2_METASTASIS);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });
      setIsSubmitting(false);
    }
  }

  const useHint = async () => {
    const hint = 'La clasificación correcta combina una presentación en el momento del diagnóstico inicial con una enfermedad que es tanto extensa como biológicamente agresiva.';
    if (hintUsed) {
      await modalHintShow({ hint, imageHintUsed, color: "orange" });
      return;
    }
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "orange" });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // se aplicará una sola vez al primer intento que compute puntos
    }
  };

  function checkAnswer() {
    if (!selectedOption) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="¿Estás seguro?" class="estasseguro" /><div class="text-center txtTitulos mt-3 orange">Debes seleccionar una opción</div>`,
      });
      return;
    }

    const option = options.find((o) => o.id === selectedOption);
    if (!option) return;

    if (blockedOptions.has(option.id)) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<div class="txt24 txt-red bold">Esa opción ya la intentaste y era incorrecta. Elige otra.</div>`,
        customConfirmButtonHtml: "Aceptar",
      });
      return;
    }

    modalConfirm({ estasseguro, color: "orange" }).then((res) => {
      if (!res.isConfirmed) return;
      setIsSubmitting(true);

      // Pista: aplicar -3 una sola vez, en el primer intento con puntos/loss
      const hintThisAttempt = (hintUsed && !hintAppliedToScore) ? hintPenalty : 0;

      // ✅ Correcta
      if (option.correct) {
        const base = (attempt === 1) ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: option.text,
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:450px' />
                     <div class="txt32 bold pt-4">Ganaste ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "orange"
        }).then(() => {
          submitAnswer(newScore, true); // único envío final
        });

        return;
      }

      // ❌ Incorrecta
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: resta -5 (y quizás -3 pista), no se envía aún
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // p.ej. -5 o -8 si hubo pista aquí
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: option.text,
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                     <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Te queda <b>1</b> intento. Elige otra opción.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "orange"
        }).then(() => {
          setPendingScore(newScore);
          setBlockedOptions(prev => new Set(prev).add(option.id));
          setAttempt(attempt + 1);
          setSelectedOption(null);
          setIsSubmitting(false);
        });

        return;
      }

      // 2º fallo: suma 0 (o -3 si justo aquí se aplica por primera vez la pista), ahora sí enviamos
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: option.text,
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                   <div class="txt32 bold pt-4">Has agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "orange"
      }).then(() => {
        submitAnswer(newScore, false); // único envío final
      });
    });
  }

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

  const shuffledOptions = useMemo(() => {
    return [...options].sort(() => Math.random() - 0.5);
  }, []);

  return (
    <div className="h-100">
      <div className="container-fluid px-5 h-100 d-flex flex-column justify-content-start pt-4 bgCircleOrange">
        <h3 className="txt30 orange bold" style={{ backgroundColor: "#FFFFFFAA", borderRadius: "10px", paddingTop: "15px"}}>
          Con los datos que tienes, ¿cómo etapificarías/clasificarías a este paciente?
        </h3>
        <div className="d-flex justify-content-between">
          <h5 className="mb-4 txt32 text-left my-2 orange">
            <IconHandClick size={40} color="#FF6017" stroke={1} /> Selecciona la respuesta correcta
          </h5>
          <div className="mb-2">
            <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
          </div>
        </div>

        <div className="row g-4">
          {shuffledOptions.map((option) => {
            const isSelected = selectedOption === option.id;
            const isBlocked = blockedOptions.has(option.id);
            return (
              <div key={option.id} className="col-md-4">
                <div
                  className={`cursor-pointer position-relative p-2 rounded shadow h-100 d-flex justify-content-center align-items-center ${isSelected ? "selected" : ""} ${isBlocked ? "opacity-50" : ""}`}
                  style={{
                    minHeight: "100px",
                    border: isSelected ? "1px solid #FF601799" : "1px solid #fff",
                    backgroundColor: isSelected ? "#FF601799" : "#fff",
                    pointerEvents: isBlocked ? "none" : "auto",
                  }}
                  onClick={() => !isBlocked && setSelectedOption(option.id)}
                  aria-disabled={isBlocked}
                  title={isBlocked ? "Opción bloqueada (ya fue incorrecta)" : "Seleccionar"}
                >
                  <div className="txt18" dangerouslySetInnerHTML={{ __html: option.text }} />
                  {isBlocked && <span className="badge bg-secondary ms-2 position-absolute top-0 end-0 m-1">Bloqueada</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between mt-4">
          <div className="position-relative d-inline-block">
            <img
              src={btnPistaOrange}
              className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
              onClick={useHint}
              alt={hintUsed ? "Pista usada" : "Usar pista"}
              title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
            />
            {hintUsed && <span className="badge-penalty">-3</span>}
          </div>
          <CheckButton
            text="Comprobar respuesta"
            onClick={checkAnswer}
            disabled={isCompleted || isSubmitting}
            color="orange"
            loading={isSubmitting}
          />
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario2Clasificacion);
